import {
  AREA_TAGS,
  can,
  canEditNote,
  canRemoveNote,
  closeLabel,
  fmtDate,
  HIT_ROLES,
  KINDS,
  missingSteps,
  projectCode,
  stepWords,
  type BoardItem,
  type BoardMember,
  type HistoryEntry,
  type ItemKind,
  type RecordedStep,
  type Stage,
  type Viewer,
} from "@boem/core/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api.ts";
import { useBoards } from "../boards.ts";
import { AiChip, Linkify } from "../util.tsx";
import { DoneByPicker, doneByModel } from "./Assign.tsx";
import { DropZone, NoteThumbs, UploadList, useMediaConfig, useUploads } from "./Media.tsx";
import { ErrorLine, Modal, SecretHint, useSecretCheck } from "./Modal.tsx";
import { dialogRef } from "./Structure.tsx";

/** "03-01 · #48", "Project 03 · #180", "#188": both numbers, so the one people say is there too */
const ref = (it: BoardItem) => dialogRef(it);

/** Run a save, keeping the modal open with the server's message if it fails. */
function useSave(onDone: () => void) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, run };
}

/** One of your notes, edited in place: the same credential check as a new note, and the history keeps what it said. */
function NoteEditor({ item, note, onDone, onSaved }: { item: BoardItem; note: BoardItem["notes"][number]; onDone: () => void; onSaved: () => void }) {
  const [text, setText] = useState(note.body);
  const { error, busy, run } = useSave(() => {
    onSaved();
    onDone();
  });
  const secrets = useSecretCheck(text);
  const same = text.trim() === note.body.trim();
  const save = () => (same ? onDone() : run(() => api.editNote(item.num, note.id, text.trim())));
  return (
    <div className="note-editor">
      <textarea
        rows={4}
        value={text}
        autoFocus
        aria-label={`Your note of ${fmtDate(note.at)}`}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !secrets.length && text.trim()) save();
          if (e.key === "Escape") {
            e.stopPropagation();
            onDone();
          }
        }}
      />
      <SecretHint findings={secrets} />
      <div className="row">
        <button type="button" className="btn" onClick={onDone}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={busy || !text.trim() || secrets.length > 0} onClick={save}>
          Save note
        </button>
      </div>
      <ErrorLine error={error} />
    </div>
  );
}

export function NoteModal({
  item,
  onClose,
  onSaved,
  editing = null,
  viewer = null,
}: {
  item: BoardItem;
  onClose: () => void;
  onSaved: () => void;
  /** a note of yours to open for editing */
  editing?: string | null;
  viewer?: Viewer | null;
}) {
  const [text, setText] = useState("");
  const [edit, setEdit] = useState<string | null>(editing);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // the note once it's saved: its screenshots upload to it after that (and a retry goes to it too)
  const [saved, setSaved] = useState<string | null>(null);
  const config = useMediaConfig();
  const files = useUploads(config);
  const mine = item.notes.filter((n) => n.mine);
  // what this viewer may do with their own notes (can(): the server checks the same)
  const mayEdit = (n: BoardItem["notes"][number]) => !viewer || canEditNote(viewer, n);
  const mayRemove = (n: BoardItem["notes"][number]) => !viewer || canRemoveNote(viewer, n);
  // screenshots go with your own note (canAttachToNote): anyone who may add files to items
  const mayAttach = !viewer || can(viewer, "attach");
  const secrets = useSecretCheck(text);
  const queued = files.list.some((u) => u.state === "queued");
  const failed = files.list.some((u) => u.state === "error" && u.kind);
  const upload = async (noteId: string, retry = false) => {
    if (await files.run(item.num, { noteId, retry })) {
      onSaved();
      onClose();
    } else {
      onSaved();
      setError("The note is saved, but some files didn't upload. Retry them, or choose Close to carry on without them.");
    }
  };
  const commit = async () => {
    if (saved) return upload(saved, true);
    if (!text.trim()) return queued ? setError("Write a few words to go with the screenshots.") : onClose();
    setBusy(true);
    setError(null);
    try {
      const r = await api.addNote(item.num, text.trim());
      setSaved(r.id);
      if (queued) await upload(r.id);
      else {
        onSaved();
        onClose();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const working = busy || files.busy;
  const workingRef = useRef(working);
  workingRef.current = working;
  // Escape / clicking outside doesn't abandon an upload halfway
  const close = useCallback(() => {
    if (!workingRef.current) onClose();
  }, [onClose]);
  const remove = (id: string) => {
    if (!window.confirm("Remove this note? The item's history keeps a line saying you removed it, and what it said.")) return;
    setRemoveError(null);
    api
      .removeNote(item.num, id)
      .then(onSaved)
      .catch((e) => setRemoveError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again."));
  };
  return (
    <Modal onClose={close}>
      <h3>Notes on {ref(item)}</h3>
      <p>{item.title}</p>
      {mine.length > 0 && (
        <>
          <label>My notes so far</label>
          <div className="notes" style={{ marginBottom: 15 }}>
            {mine.map((n) =>
              edit === n.id ? (
                <NoteEditor key={n.id} item={item} note={n} onDone={() => setEdit(null)} onSaved={onSaved} />
              ) : (
                <div className="note" key={n.id}>
                  <div style={{ flex: 1 }}>
                    <div className="meta">{fmtDate(n.at)}</div>
                    <div className="body">
                      <Linkify text={n.body} />
                    </div>
                    <NoteThumbs shots={item.shots} noteId={n.id} />
                  </div>
                  <div className="note-btns">
                    {mayEdit(n) && (
                      <button className="note-btn" type="button" aria-label={`Edit your note of ${fmtDate(n.at)}`} onClick={() => setEdit(n.id)}>
                        Edit
                      </button>
                    )}
                    {mayRemove(n) && (
                      <button className="note-btn" type="button" aria-label={`Remove your note of ${fmtDate(n.at)}`} onClick={() => remove(n.id)}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ),
            )}
          </div>
          <ErrorLine error={removeError} />
        </>
      )}
      <label>{mine.length ? "Add another note" : "Your note"}</label>
      <textarea
        rows={4}
        value={text}
        disabled={!!saved}
        placeholder="Reference other items with #12 and they will become links."
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && !secrets.length && !working && void commit()}
      />
      <SecretHint findings={secrets} />
      {mayAttach && (
        <>
          <DropZone onFiles={files.add} config={config} disabled={!!saved} />
          <UploadList list={files.list} onRemove={saved ? undefined : files.remove} />
        </>
      )}
      <div className="row">
        <button className="btn" disabled={working} onClick={onClose}>
          Close
        </button>
        {(!saved || failed) && (
          <button className="btn primary" disabled={working || secrets.length > 0} onClick={() => void commit()}>
            {saved ? "Retry" : queued ? "Add note and upload" : "Add note"}
          </button>
        )}
      </div>
      <ErrorLine error={error} />
      <div className="hint">
        Each note is kept separately with its own date, so a thread builds up instead of one note being overwritten. You can edit or remove your own notes; the item&rsquo;s
        history keeps what they said.
        {mayAttach && " Screenshots added here show under your note once they're checked for passwords, keys and tokens; removing the note removes them too."}
      </div>
    </Modal>
  );
}

/**
 * Edit an item's title and description (the … menu, for whoever logged it and leads: canEditItem).
 * The same credential check as a new item; the history keeps the old title and description.
 */
export function EditItemModal({ item, onClose, onSaved }: { item: BoardItem; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(item.title);
  const [body, setBody] = useState(item.body);
  const { error, busy, run } = useSave(() => {
    onSaved();
    onClose();
  });
  const secrets = useSecretCheck(title, body);
  const edit = {
    ...(title.trim() !== item.title ? { title: title.trim() } : {}),
    ...(body.trim() !== item.body.trim() ? { body: body.trim() } : {}),
  };
  const changed = Object.keys(edit).length > 0;
  const commit = () => (changed ? run(() => api.editItem(item.num, edit)) : onClose());
  return (
    <Modal onClose={onClose} wide>
      <h3>Edit {ref(item)}</h3>
      <p>Fix the wording. Everyone sees the new title and description straight away; the history keeps the old ones, with your name.</p>
      <label htmlFor={`edit-title-${item.num}`}>Title</label>
      <input id={`edit-title-${item.num}`} type="text" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      <label htmlFor={`edit-body-${item.num}`} style={{ marginTop: 12 }}>
        What happens
      </label>
      <textarea
        id={`edit-body-${item.num}`}
        rows={8}
        value={body}
        placeholder="Steps, what you expected, what you saw. Reference other items with #12."
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && !secrets.length && title.trim().length >= 3 && commit()}
      />
      <SecretHint findings={secrets} />
      <div className="row">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy || title.trim().length < 3 || secrets.length > 0 || !changed} onClick={commit}>
          Save
        </button>
      </div>
      <ErrorLine error={error} />
    </Modal>
  );
}

/** What closing says, by kind: a task is marked done, a project is closed as a project. */
const CLOSE_COPY: Record<"item" | "task" | "project", { title: string; label: string; placeholder: string; hint: string }> = {
  item: {
    title: "Close {ref}",
    label: "How was it resolved? (optional)",
    placeholder: "e.g. shipped in the 8/15 build, or dismissed - not a bug.",
    hint: "Closing is recorded against your name, so everyone sees who closed it and why.",
  },
  task: {
    title: "Mark {ref} done",
    label: "What was done? (optional)",
    placeholder: "e.g. ran the script against the 10/02 order; output in the note above.",
    hint: "A task goes To do → Done: it moves to Closed with your name, and never to the deploy or verify queue. Reopen it if more turns up.",
  },
  project: {
    title: "Close project {ref}",
    label: "Why is it closed? (optional)",
    placeholder: "e.g. everything in it shipped with the 10/15 release.",
    hint: "Its items stay where they are, with their numbers. A lead can archive the project once it is closed.",
  },
};

export function CloseModal({ item, onClose, onSaved }: { item: BoardItem; onClose: () => void; onSaved: () => void }) {
  const [text, setText] = useState("");
  const { error, busy, run } = useSave(() => {
    onSaved();
    onClose();
  });
  const secrets = useSecretCheck(text);
  const commit = () => run(() => api.setStatus(item.num, "closed", text.trim() || undefined));
  const copy = CLOSE_COPY[item.kind === "task" || item.kind === "project" ? item.kind : "item"];
  return (
    <Modal onClose={onClose}>
      <h3>{copy.title.replace("{ref}", ref(item))}</h3>
      <p>{item.title}</p>
      <label>{copy.label}</label>
      <textarea
        rows={4}
        value={text}
        placeholder={copy.placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && !secrets.length && commit()}
      />
      <SecretHint findings={secrets} />
      <div className="row">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy || secrets.length > 0} onClick={commit}>
          {closeLabel(item.kind, "open")}
        </button>
      </div>
      <ErrorLine error={error} />
      <div className="hint">{copy.hint}</div>
    </Modal>
  );
}

const STAGE_COPY: Record<Stage, { title: string; label: string; placeholder: string; button: string; hint: string }> = {
  developed: {
    title: "Mark {ref} developed",
    label: "What changed? (optional)",
    placeholder: "e.g. PR #88 - the Orders page now uses the listing's image",
    button: "Mark developed",
    hint: "It moves to the deploy queue. Once it's deployed, someone other than you checks the fix.",
  },
  deployed: {
    title: "Mark {ref} deployed",
    label: "Where, or in which release? (optional)",
    placeholder: "e.g. production, release 2.3 on 9/30",
    button: "Mark deployed",
    hint: "It moves to the verify queue for someone to check the fix.",
  },
  verified: {
    title: "Verify {ref}",
    label: "How did you check it? (optional)",
    placeholder: "e.g. placed a test order on the phone - the images show",
    button: "Verified",
    hint: "It's closed and moves to the archive, with your name and today's date. It no longer shows on the board.",
  },
  todo: {
    title: "Send {ref} back",
    label: "What's still wrong?",
    placeholder: "e.g. still no image for kits on iOS",
    button: "Send back",
    hint: "",
  },
};

/** The question when steps were never marked: "Not marked developed or deployed yet. Who developed and deployed it?" */
export const doneByQuestion = (missing: RecordedStep[]) => `Not marked ${missing.join(" or ")} yet. Who ${stepWords(missing)} it?`;

/**
 * Move an item along the fix flow, or send it back to To do with what's still wrong. Verifying
 * (or marking deployed) an item whose earlier steps nobody marked asks a lead or a verifier who
 * did them: one person, required, recorded on each of those steps.
 */
export function StageModal({
  item,
  to,
  viewer,
  members,
  onClose,
  onSaved,
}: {
  item: BoardItem;
  to: Stage;
  viewer: Viewer;
  members: BoardMember[];
  onClose: () => void;
  onSaved: (r: { archived: boolean }) => void;
}) {
  const [text, setText] = useState("");
  const missing = can(viewer, "stage_for") ? missingSteps(item, to) : [];
  const people = useMemo(() => doneByModel(item, members), [item, members]);
  const [doneBy, setDoneBy] = useState<string | null>(people.preselect);
  const archived = useRef(false);
  const { error, busy, run } = useSave(() => {
    onSaved({ archived: archived.current });
    onClose();
  });
  const secrets = useSecretCheck(text);
  const copy = STAGE_COPY[to];
  const back = to === "todo";
  const asks = missing.length > 0;
  const blocked = busy || secrets.length > 0 || (back && !text.trim()) || (asks && !doneBy);
  const commit = () =>
    !blocked &&
    run(async () => {
      archived.current = (await api.setStage(item.num, to, text.trim() || undefined, asks ? doneBy! : undefined)).archived;
    });
  const doer = members.find((m) => m.id === doneBy)?.name;
  const dev = item.developed?.by;
  const hint = back
    ? `It goes back to To do${dev ? ` with ${dev}` : item.assignee ? ` with ${item.assignee.name}` : ""}, and your note becomes the next step.`
    : copy.hint;
  return (
    <Modal onClose={onClose}>
      <h3>{copy.title.replace("{ref}", ref(item))}</h3>
      <p>{item.title}</p>
      {to === "verified" && item.deployed && (
        <p className="stage-ctx">
          Deployed {fmtDate(item.deployed.at)}
          {item.deployed.by ? ` by ${item.deployed.by}` : ""}
          {item.deployed.note ? ` — ${item.deployed.note}` : ""}
        </p>
      )}
      {asks && (
        <section className="doneby" aria-labelledby={`doneby-q-${item.num}`}>
          <h4 id={`doneby-q-${item.num}`}>
            {doneByQuestion(missing)} <span className="req">Required</span>
          </h4>
          <p className="doneby-hint">
            {doer ? `${doer} is recorded as having ${stepWords(missing)} it, today.` : `Pick who ${stepWords(missing)} it: they're recorded as having done it, today.`} The
            history says you recorded it.
          </p>
          <DoneByPicker model={people} value={doneBy} onChange={setDoneBy} label={doneByQuestion(missing)} viewerId={viewer.id} />
        </section>
      )}
      <label>{copy.label}</label>
      <textarea
        rows={3}
        value={text}
        placeholder={copy.placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && commit()}
      />
      <SecretHint findings={secrets} />
      <div className="row">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={blocked} onClick={commit}>
          {copy.button}
        </button>
      </div>
      <ErrorLine error={error} />
      <div className="hint">{hint}</div>
    </Modal>
  );
}

/** Where a history line came from (the History dialog, and an item panel's history). */
export const SOURCE_LABEL: Record<string, string> = {
  web: "web",
  mcp: "via Claude",
  claude_code: "Claude Code session",
  github: "GitHub",
  system: "system",
  import: "original board",
  teams: "Teams",
};

export function HistoryModal({ item, onClose }: { item: BoardItem; onClose: () => void }) {
  const [rows, setRows] = useState<HistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.history(item.num).then(setRows, (e) => setError(e instanceof ApiError ? e.message : "Couldn't load the history."));
  }, [item.num]);
  return (
    <Modal onClose={onClose} wide>
      <h3>History of {ref(item)}</h3>
      <p>{item.title}</p>
      {!rows && !error && <p className="count-line">Loading&hellip;</p>}
      {rows && (
        <ol className="hist">
          {rows.map((r, i) => (
            <li key={i}>
              <span className="hist-when">{fmtDate(r.at)}</span>
              <span className="hist-what">
                <b>{r.actor ?? "Someone"}</b> {r.agent && <AiChip agent={r.agent} />} <Linkify text={r.text} />
                <NoteThumbs shots={item.shots} noteId={r.noteId} />
              </span>
              <span className="hist-src">{SOURCE_LABEL[r.source] ?? r.source}</span>
            </li>
          ))}
        </ol>
      )}
      <ErrorLine error={error} />
      <div className="row">
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

/** How the New item dialog names where it goes: "03" for a project, "03-01" (or #48) for an item. */
const placeRef = (it: BoardItem) => (it.kind === "project" && it.projectNo != null ? projectCode(it.projectNo) : (it.key?.label ?? `#${it.num}`));

export function NewItemModal({
  onClose,
  onCreated,
  parent = null,
  feature = false,
  presetKind = null,
  viewer,
}: {
  onClose: () => void;
  onCreated: (num: number) => void;
  /** where it goes: a project, or the item it's a sub-item of */
  parent?: BoardItem | null;
  /** a feature request (Request a feature): starts as a Feature */
  feature?: boolean;
  /** what it starts as (+ New project in the sidebar: a project); it can still be changed */
  presetKind?: ItemKind | null;
  viewer: Viewer;
}) {
  const [kind, setKind] = useState<ItemKind | null>(presetKind ?? (feature ? "feature" : null));
  // a personal board says so: it's yours, not everyone's
  const personal = useBoards()?.org.kind === "personal";
  // only a lead can start a project, and only at the top
  const kinds = KINDS.filter((k) => k.id !== "project" || (!parent && can(viewer, "project")));
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [tags, setTags] = useState<string[]>([]);
  const [role, setRole] = useState("");
  // its due date (OEM-267): "" for none
  const [due, setDue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // set once the item exists; screenshots are uploaded to it after that
  const [created, setCreated] = useState<number | null>(null);
  const config = useMediaConfig();
  const files = useUploads(config);
  const secrets = useSecretCheck(title, body);
  const toggle = (t: string) => setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const finish = (num: number) => {
    onCreated(num);
    onClose();
  };
  const upload = async (num: number, retry = false) => {
    setError(null);
    if (await files.run(num, { retry })) finish(num);
    else setError(`Logged as #${num}, but some files didn't upload. Retry them, or choose Done to carry on without them.`);
  };
  const commit = async (draft = false) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.createItem({ title: title.trim(), body: body.trim(), priority, tags, role: role || null, kind, parent: parent?.num ?? null, due: due || null, ...(draft ? { draft: true } : {}) });
      setCreated(r.num);
      if (files.list.some((u) => u.state === "queued")) await upload(r.num);
      else finish(r.num);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const working = busy || files.busy;
  // files waiting for the item: they upload to it as soon as it's logged
  const waiting = files.list.filter((u) => u.state === "queued").length;
  const state = useRef({ working, created });
  state.current = { working, created };
  // Escape / outside click: never abandon an upload halfway; after the item exists, land on it
  const close = useCallback(() => {
    const s = state.current;
    if (s.working) return;
    if (s.created) onCreated(s.created);
    onClose();
  }, [onClose, onCreated]);
  const locked = created !== null;
  return (
    <Modal onClose={close} wide>
      <h3>
        {locked
          ? `Logged as #${created}`
          : feature
            ? "Request a feature"
            : parent
              ? parent.kind === "project"
                ? "New item in a project"
                : "New sub-item"
              : presetKind === "project"
                ? "New project"
                : personal
                  ? "New item on your board"
                  : "Log a new item"}
      </h3>
      <p>
        {parent ? (
          <>
            {parent.kind === "project" ? "In project" : "A sub-item of"} <b>{placeRef(parent)} {parent.title}</b>
            {parent.kind !== "project" && parent.project ? ` (in ${parent.project.projectNo != null ? `project ${projectCode(parent.project.projectNo)}` : `#${parent.project.num}`} ${parent.project.title})` : ""}.
          </>
        ) : (
          <>
            {personal
              ? kind === "project"
                ? "It gets your board's next project number (01, 02, ...) and numbers the items put in it (01-07). Only the people on your board see it."
                : "It gets your board's next number. Only the people on your board see it; nothing shows on OEMUAP."
              : kind === "project"
                ? "It gets the next project number (01, 02, ...) and numbers the items put in it (01-07). It shows up in everyone's sidebar straight away."
                : "It gets the next number and shows up on everyone’s board straight away."}
          </>
        )}
      </p>
      <label>What it is</label>
      <div className="asg-chips" role="radiogroup" aria-label="What it is">
        {kinds.map((k) => (
          <button key={k.id} type="button" className="chip" role="radio" aria-checked={kind === k.id} aria-pressed={kind === k.id} disabled={locked} title={k.hint} onClick={() => setKind(kind === k.id ? null : k.id)}>
            {k.label}
          </button>
        ))}
      </div>
      <label style={{ marginTop: 12 }}>Title</label>
      <input type="text" value={title} maxLength={200} disabled={locked} placeholder="Short summary of the problem" onChange={(e) => setTitle(e.target.value)} />
      <label style={{ marginTop: 12 }}>What happens</label>
      <textarea
        rows={6}
        value={body}
        disabled={locked}
        placeholder="Steps, what you expected, what you saw. Reference other items with #12."
        onChange={(e) => setBody(e.target.value)}
      />
      <SecretHint findings={secrets} />
      <label style={{ marginTop: 12 }}>Screenshots and recordings</label>
      <DropZone onFiles={files.add} config={config} disabled={locked} />
      <UploadList list={files.list} onRemove={locked ? undefined : files.remove} />
      {waiting > 0 && !locked && <div className="dz-wait">They&rsquo;re added to the item as soon as it&rsquo;s logged.</div>}
      <div className="form-grid">
        <div>
          <label>Priority</label>
          <select className="search" value={priority} disabled={locked} onChange={(e) => setPriority(e.target.value)}>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div>
          <label>Who hits it</label>
          <select className="search" value={role} disabled={locked} onChange={(e) => setRole(e.target.value)}>
            <option value="">&mdash;</option>
            {HIT_ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="new-due">Due date</label>
          <input id="new-due" type="date" className="due-input" value={due} disabled={locked} onChange={(e) => setDue(e.target.value)} />
        </div>
      </div>
      <label style={{ marginTop: 12 }}>Where</label>
      <div className="asg-chips">
        {AREA_TAGS.map((t) => (
          <button key={t.id} type="button" className="chip" disabled={locked} aria-pressed={tags.includes(t.id)} onClick={() => toggle(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="row">
        {!locked ? (
          <>
            <button className="btn" disabled={working} onClick={onClose}>
              Cancel
            </button>
            {/* drafts (OEM-286): not ready to discuss with the team yet - hidden from others until it's made ready */}
            {can(viewer, "draft") && (
              <button className="btn" disabled={working || title.trim().length < 3 || secrets.length > 0} title="Hidden from others until you make it ready for the team" onClick={() => void commit(true)}>
                Save as draft
              </button>
            )}
            <button className="btn primary" disabled={working || title.trim().length < 3 || secrets.length > 0} onClick={() => void commit()}>
              {waiting ? `Log item and upload ${waiting === 1 ? "1 file" : `${waiting} files`}` : "Log item"}
            </button>
          </>
        ) : (
          <>
            {files.list.some((u) => u.state === "error" && u.kind) && (
              <button className="btn" disabled={working} onClick={() => void upload(created!, true)}>
                Retry
              </button>
            )}
            <button className="btn primary" disabled={working} onClick={() => finish(created!)}>
              Done
            </button>
          </>
        )}
      </div>
      <ErrorLine error={error} />
      <div className="hint">
        Screenshots and recordings are checked for passwords, keys and tokens before the rest of the team sees them; blur anything secret first.
        Location and camera details are removed.
      </div>
    </Modal>
  );
}
